import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useGeolocation } from "@/lib/useGeolocation";
import { LocateFixed } from "lucide-react";

/**
 * Reusable workshop picker used on the product, diagnose and verksted pages.
 *
 * Loads `/api/fixers` with the supplied `extraParams` and re-loads when the
 * user types a postcode or clicks "Bruk min posisjon". Sorting is done by
 * the backend (real distance when Google Maps key works, otherwise postal
 * prefix fallback).
 *
 * Props:
 *  - extraParams: object — e.g. {category:"iPhone"} or {product_id:"..."}
 *  - onLoad(fixers) — callback whenever the list refreshes
 *  - renderItem(fixer, isSelected, onSelect) — caller controls per-row UI
 *  - selectedId — currently-selected fixer id
 *  - onSelect(id) — selection callback
 *  - emptyMessage — text shown when zero fixers come back
 */
export default function WorkshopPicker({
  extraParams = {},
  onLoad,
  renderItem,
  selectedId,
  onSelect,
  emptyMessage = "Ingen verksteder tilgjengelig akkurat nå — nye åpner snart.",
}) {
  const [postal, setPostal] = useState("");
  const [coords, setCoords] = useState(null); // {latitude, longitude}
  const [fixers, setFixers] = useState([]);
  const [loading, setLoading] = useState(false);
  const geo = useGeolocation();

  const stableExtra = JSON.stringify(extraParams);

  const refresh = useCallback(() => {
    const params = { ...extraParams };
    if (coords) {
      params.latitude = coords.latitude;
      params.longitude = coords.longitude;
    } else if (postal) {
      params.postal_code = postal;
    }
    setLoading(true);
    api
      .get("/fixers", { params })
      .then((r) => {
        setFixers(r.data);
        // Default-select the nearest one when nothing is selected yet
        if (r.data.length > 0 && onSelect) {
          const stillValid = selectedId && r.data.some((f) => f.id === selectedId);
          if (!stillValid) onSelect(r.data[0].id);
        } else if (r.data.length === 0 && onSelect) {
          onSelect("");
        }
        onLoad?.(r.data);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postal, coords, stableExtra]);

  useEffect(() => { refresh(); }, [refresh]);

  const useMyPosition = async () => {
    try {
      const c = await geo.getCurrentPosition();
      setCoords(c);
      setPostal(""); // clear typed postcode so coords take priority
    } catch (err) {
      // The geolocation hook already surfaces a Norwegian toast — log for devs.
      console.warn("Geolocation failed", err);
    }
  };

  const onPostalChange = (e) => {
    setCoords(null);
    setPostal(e.target.value);
  };

  return (
    <div data-testid="workshop-picker">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <input
          placeholder="Postnummer (f.eks. 0184)"
          value={postal}
          onChange={onPostalChange}
          className="tk-input max-w-[240px]"
          data-testid="postal-input"
        />
        <button
          type="button"
          onClick={useMyPosition}
          disabled={geo.busy}
          className="tk-btn-secondary !py-2 !px-3 text-xs"
          data-testid="use-my-location-btn"
        >
          <LocateFixed size={14} />
          {geo.busy ? "Henter …" : "Bruk min posisjon"}
        </button>
        {coords && (
          <span className="text-xs text-emerald-600">
            Bruker GPS-posisjon
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500" data-testid="workshop-loading">Laster verksteder …</p>
      ) : fixers.length === 0 ? (
        <div
          className="tk-card text-center text-sm text-slate-500 py-8"
          data-testid="workshop-empty"
        >
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-2" data-testid="workshop-list">
          {fixers.map((f) =>
            renderItem(f, f.id === selectedId, () => onSelect?.(f.id)),
          )}
        </div>
      )}
    </div>
  );
}
